import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  FileText,
  Inbox,
  MessageSquare,
  Settings,
  Mail,
  LogOut,
  Database,
  Shield,
  ScrollText,
  UserCog,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/candidates", label: "Candidates", icon: FileText },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/contacts", label: "Messages", icon: MessageSquare },
  { to: "/admin/users", label: "Users", icon: Users, adminOnly: true },
  { to: "/admin/email-templates", label: "Email Templates", icon: Mail, adminOnly: true },
  { to: "/admin/cms", label: "Website Content", icon: Settings, adminOnly: true },
  { to: "/admin/privacy-policy", label: "Privacy Policy", icon: ScrollText, adminOnly: true },
  { to: "/admin/data-management", label: "Data Management", icon: Database, adminOnly: true },
  { to: "/admin/security", label: "Security", icon: Shield, adminOnly: true },
  { to: "/admin/account", label: "My Account", icon: UserCog },
];

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-obsidian text-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-2">
          <div className="h-6 w-6 bg-execution" />
          <div>
            <div className="font-heading text-lg font-bold leading-none">Nexra</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mt-1 flex items-center gap-1.5">
              <span className="status-dot" /> Admin
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-execution text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3">
            <div className="text-sm font-medium truncate" data-testid="admin-user-name">{user?.name}</div>
            <div className="text-xs text-white/50 truncate">{user?.email}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-execution mt-1">
              {user?.role}
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider border border-white/20 hover:border-execution hover:text-execution transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Log out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <main className="flex-1 min-w-0">
        {/* Mobile bar */}
        <div className="lg:hidden sticky top-0 z-40 bg-obsidian text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-execution" />
            <span className="font-heading font-bold">Nexra Admin</span>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-mobile"
            className="text-xs font-mono uppercase tracking-wider"
          >
            Log out
          </button>
        </div>
        <div className="lg:hidden border-b border-border bg-white px-4 py-3 flex gap-1 overflow-x-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `shrink-0 px-3 py-1.5 text-xs font-medium whitespace-nowrap border ${
                  isActive
                    ? "bg-obsidian text-white border-obsidian"
                    : "text-muted-foreground border-border"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="px-6 sm:px-10 lg:px-12 py-8 lg:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <div className="overline mb-2">{user?.role === "admin" ? "Admin" : "Staff"}</div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight" data-testid="admin-page-title">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>
              )}
            </div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
